import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { FaCheck } from "react-icons/fa";
import { db } from "../Configuration/firebase";
import {
  createEvent,
  VISIBILITY_OPTIONS,
  DEFAULT_TTL_MS,
} from "../utils/events";
import AddressPicker from "./AddressPicker";
import "../CSS/App.css";

// Host flow, split into two light steps so the screen never crowds:
//   1. Choose the location (the AddressPicker: search, saved list, locate).
//   2. Add details & share: a compact summary of the chosen spot, an optional
//      name, and how long the address stays visible - then Generate Code.
// The AddressPicker stays mounted (step 1 is just hidden on step 2) so going
// Back keeps the host's pick intact.
const StartEvent = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [step, setStep] = useState(1);
  const [eventName, setEventName] = useState("");
  const [selected, setSelected] = useState(null); // { addressTitle, address }
  const [ttlMs, setTtlMs] = useState(DEFAULT_TTL_MS);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }
      setUser(currentUser);
      try {
        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (userSnap.exists()) setUserInfo(userSnap.data());
      } catch (err) {
        console.error("Error loading Start Event screen:", err);
        setError("Couldn't load the page. Please try again.");
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  const goToDetails = () => {
    if (!selected) {
      setError("Pick an address (or use your current location) to share.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleStart = async () => {
    if (!selected) {
      setStep(1);
      setError("Pick an address (or use your current location) to share.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const code = await createEvent({
        host: { uid: user.uid, ...(userInfo || {}) },
        name: eventName,
        addressTitle: selected.addressTitle,
        address: selected.address,
        ttlMs,
      });
      navigate(`/event/${code}`);
    } catch (err) {
      console.error("Error creating event:", err);
      setError(err.message || "Couldn't start the event. Please try again.");
      setCreating(false);
    }
  };

  const labelStyle = {
    marginBottom: "8px",
    fontSize: "15px",
    fontWeight: 600,
  };
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid var(--border-color, #ccc)",
    backgroundColor: "var(--item-bg, #fff)",
    color: "var(--text-primary, #1a1a1a)",
    fontSize: "15px",
  };
  const primaryBtn = (enabled) => ({
    padding: "12px 24px",
    border: "none",
    cursor: enabled ? "pointer" : "default",
    borderRadius: "8px",
    backgroundColor: enabled ? "#4CAF50" : "#9ccc9e",
    color: "white",
    fontSize: "15px",
    fontWeight: 600,
  });
  const secondaryBtn = {
    backgroundColor: "transparent",
    color: "var(--text-secondary, #777)",
    padding: "12px 18px",
    border: "1px solid var(--border-color, #ccc)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "15px",
  };

  return (
    <div
      className="container"
      style={{
        width: "85%",
        maxWidth: "420px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2 style={{ marginBottom: "4px" }}>Start an event</h2>
      <p
        style={{
          color: "var(--text-secondary, #777)",
          fontSize: "13px",
          marginTop: 0,
          letterSpacing: "0.02em",
        }}
      >
        {step === 1
          ? "Step 1 of 2 · Choose the location"
          : "Step 2 of 2 · Add details & share"}
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {/* STEP 1 - pick the location. Kept mounted (just hidden) so Back
              preserves the pick. */}
          <div style={{ display: step === 1 ? "block" : "none" }}>
            <div style={labelStyle}>Location to share</div>
            <AddressPicker uid={user?.uid} onSelect={setSelected} />

            <div style={{ display: "flex", gap: "12px", marginTop: "14px" }}>
              <button
                type="button"
                onClick={goToDetails}
                disabled={!selected}
                style={primaryBtn(!!selected)}
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                style={secondaryBtn}
              >
                Cancel
              </button>
            </div>
          </div>

          {/* STEP 2 - details & share. */}
          {step === 2 && (
            <div>
              {/* Chosen location summary, with a way back to change it. */}
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: "10px",
                  backgroundColor: "var(--chip-bg, #f0f0f0)",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary, #777)",
                    marginBottom: "2px",
                  }}
                >
                  Location to share
                </div>
                {selected?.addressTitle && (
                  <div style={{ fontWeight: 600 }}>{selected.addressTitle}</div>
                )}
                <div style={{ fontSize: "14px" }}>{selected?.address}</div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "6px 0 0",
                    marginTop: "2px",
                    cursor: "pointer",
                    color: "#007BFF",
                    fontSize: "13px",
                  }}
                >
                  Change
                </button>
              </div>

              {/* Optional name. */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  Event name{" "}
                  <span
                    style={{
                      color: "var(--text-secondary, #777)",
                      fontWeight: 400,
                    }}
                  >
                    (optional)
                  </span>
                </div>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  maxLength={60}
                  placeholder="e.g. Sarah's Birthday"
                  style={inputStyle}
                />
              </div>

              {/* Visibility window (the privacy control). */}
              <div style={{ marginBottom: "8px" }}>
                <div style={labelStyle}>Address visible for</div>
                <div role="radiogroup" aria-label="Address visible for">
                  {VISIBILITY_OPTIONS.map((o) => {
                    const isSel = ttlMs === o.ms;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        role="radio"
                        aria-checked={isSel}
                        onClick={() => setTtlMs(o.ms)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                          width: "100%",
                          textAlign: "left",
                          padding: "12px 14px",
                          marginBottom: "8px",
                          borderRadius: "10px",
                          border: isSel
                            ? "2px solid #4CAF50"
                            : "1px solid var(--border-color, #eee)",
                          backgroundColor: isSel
                            ? "rgba(76, 175, 80, 0.10)"
                            : "var(--chip-bg, #f6f6f6)",
                          color: "var(--text-primary, #1a1a1a)",
                          cursor: "pointer",
                        }}
                      >
                        <span>
                          <span
                            style={{
                              fontSize: "15px",
                              fontWeight: isSel ? 700 : 600,
                            }}
                          >
                            {o.label}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "var(--text-secondary, #777)",
                            }}
                          >
                            {o.hint}
                          </span>
                        </span>
                        {isSel && <FaCheck color="#4CAF50" size={15} />}
                      </button>
                    );
                  })}
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary, #999)",
                    margin: "2px 2px 0",
                    lineHeight: 1.4,
                  }}
                >
                  Auto-expiry keeps your address from being visible forever. You
                  can end the event any time.
                </p>
              </div>

              {error && <p style={{ color: "red" }}>{error}</p>}

              <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={creating}
                  style={primaryBtn(!creating)}
                >
                  {creating ? "Starting..." : "Generate code"}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={creating}
                  style={secondaryBtn}
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
};

export default StartEvent;
